import { motion } from "framer-motion";
import { Clock, IndianRupee, Download, Map, Users, PieChart, User } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Expense } from "@/types/expense";
import { TripExpenseDetails } from "./TripExpenseDetails";
import { generateTripReport } from "@/utils/pdfGenerator";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";

interface TripCardProps {
  trip: {
    name: string;
    expenses: Expense[];
    totalAmount: number;
    startDate: string;
    endDate: string;
    stats?: {
      totalParticipants: number;
      categoryBreakdown: Record<string, number>;
      topSpender: [string, number];
    };
  };
  selectedTrip: string | null;
  onTripSelect: (tripName: string | null) => void;
}

export const TripCard = ({
  trip,
  selectedTrip,
  onTripSelect,
}: TripCardProps) => {
  const handleExportPDF = () => {
    try {
      generateTripReport(trip.name, trip.expenses);
      toast.success("Report Generated", {
        description: `PDF report for ${trip.name} has been downloaded.`
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate report. Please try again.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-4"
    >
      <Card className="p-4 md:p-6 bg-gradient-card hover:shadow-lg transition-all overflow-hidden">
        <div className="flex flex-col md:flex-row justify-between items-start gap-4 mb-4">
          <div className="space-y-2 w-full md:w-auto">
            <div className="flex items-center gap-2">
              <Map className="h-5 w-5 text-primary animate-pulse" />
              <motion.h3 
                className="text-xl font-semibold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent truncate max-w-[200px] md:max-w-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {trip.name}
              </motion.h3>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4 flex-shrink-0" />
              <p className="truncate">
                {format(new Date(trip.startDate), "PPP")} -{" "}
                {format(new Date(trip.endDate), "PPP")}
              </p>
            </div>
            {trip.stats && (
              <ScrollArea className="w-full whitespace-nowrap">
                <div className="flex gap-2 py-2">
                  <Badge variant="outline" className="flex items-center gap-1 flex-shrink-0">
                    <Users className="h-3 w-3" />
                    {trip.stats.totalParticipants} participants
                  </Badge>
                  <Badge variant="outline" className="flex items-center gap-1 flex-shrink-0">
                    <User className="h-3 w-3" />
                    Top spender: {trip.stats.topSpender[0]}
                  </Badge>
                  <Badge variant="outline" className="flex items-center gap-1 flex-shrink-0">
                    <PieChart className="h-3 w-3" />
                    {Object.keys(trip.stats.categoryBreakdown).length} categories
                  </Badge>
                </div>
                <ScrollBar orientation="horizontal" className="opacity-0 hover:opacity-100 transition-opacity" />
              </ScrollArea>
            )}
          </div>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 w-full md:w-auto">
            <div className="text-left md:text-right w-full md:w-auto">
              <p className="text-sm text-muted-foreground">Total Spent</p>
              <motion.p 
                className="text-xl font-bold text-primary flex items-center"
                whileHover={{ scale: 1.05 }}
              >
                <IndianRupee className="h-4 w-4" />
                {Number(trip.totalAmount).toFixed(2)}
              </motion.p>
            </div>
            <motion.div whileHover={{ scale: 1.05 }} className="w-full md:w-auto">
              <Button
                variant="outline"
                size="sm"
                className="w-full md:w-auto flex items-center gap-2 bg-gradient-to-r from-primary/10 to-primary/5"
                onClick={handleExportPDF}
              >
                <Download className="h-4 w-4" />
                Export PDF
              </Button>
            </motion.div>
          </div>
        </div>

        <Button
          variant="ghost"
          className="w-full text-left justify-start hover:bg-primary/5"
          onClick={() => onTripSelect(selectedTrip === trip.name ? null : trip.name)}
        >
          {selectedTrip === trip.name ? "Hide Details" : "Show Details"}
        </Button>

        {selectedTrip === trip.name && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 space-y-4"
          >
            {trip.stats && (
              <ScrollArea className="w-full">
                <div className="grid grid-cols-1 gap-4 min-w-[300px]">
                  <Card className="p-4 bg-white/50 backdrop-blur-sm">
                    <h4 className="font-semibold mb-2">Category Breakdown</h4>
                    <div className="space-y-2">
                      {Object.entries(trip.stats.categoryBreakdown).map(([category, amount]) => (
                        <div key={category} className="flex justify-between items-center">
                          <span className="truncate mr-2">{category}</span>
                          <span className="font-medium flex-shrink-0">₹{amount.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
                <ScrollBar orientation="horizontal" className="opacity-0 hover:opacity-100 transition-opacity" />
              </ScrollArea>
            )}
            <ScrollArea className="w-full">
              <div className="space-y-4 min-w-[300px]">
                {trip.expenses.map((expense) => (
                  <TripExpenseDetails key={expense.id} expense={expense} />
                ))}
              </div>
              <ScrollBar orientation="horizontal" className="opacity-0 hover:opacity-100 transition-opacity" />
            </ScrollArea>
          </motion.div>
        )}
      </Card>
    </motion.div>
  );
};